﻿import axios, { AxiosInstance, AxiosResponse } from "axios";
import { ApiResult } from "@eaigle/types";

/** Extracts a backend-provided error message (e.g. from a 409 Conflict) for display, if any. */
export function getErrorMessage(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const data = error.response?.data as { message?: string; Message?: string } | undefined;
  return data?.message ?? data?.Message;
}

let currentAuthToken: string | null = null;

export function setAuthToken(token: string | null): void {
  currentAuthToken = token;
}

export function getAuthToken() {
  return currentAuthToken;
}

export const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
  Accept: "application/json",
});

function isApiResult<T>(data: unknown): data is ApiResult<T> {
  if (!data) return false;
  if (typeof data !== "object") return false;
  return "data" in data || "message" in data;
}

function unwrapApiResult<T>(axiosResponse: AxiosResponse<T | ApiResult<T>>): T {
  // AxiosResponse wraps the result with some meta data and
  // the actual result is in the `data` field. But, some of
  // our API calls contain their own wrapper inside of that
  // so we have a nested `data`. Our intention is to remove
  // this double layer... but for now, handle both cleanly.
  // Ah, Carriers has a triple layer!
  let { data } = axiosResponse;
  while (isApiResult<T>(data)) {
    if (
      (typeof data.success === "boolean" && !data.success) ||
      (typeof data.message === "string" && typeof data.data === "undefined") // e.g. when no lane events were found
    ) {
      throw new Error(data.message);
    }
    data = data.data;
  }
  return data;
}

export async function requestApiResult<T>(
  axiosInstance: AxiosInstance,
  token: string,
  endpoint: string,
  method: "get" | "delete",
): Promise<T> {
  try {
    const axiosResponse = await axiosInstance[method]<T | ApiResult<T>>(endpoint, {
      headers: createHeaders(token),
    });
    return unwrapApiResult(axiosResponse);
  } catch (error) {
    console.error(`Error ${method}ing ${endpoint}:`, error);
    throw error;
  }
}

export async function getApiResult<T>(
  axiosInstance: AxiosInstance,
  token: string,
  endpoint: string,
): Promise<T> {
  return await requestApiResult(axiosInstance, token, endpoint, "get");
}

export async function deleteApiResult(
  axiosInstance: AxiosInstance,
  token: string,
  endpoint: string,
): Promise<void> {
  return await requestApiResult(axiosInstance, token, endpoint, "delete");
}

export async function requestApiResultWithData<T, U>(
  axiosInstance: AxiosInstance,
  token: string,
  endpoint: string,
  data: T,
  method: "post" | "put",
): Promise<U> {
  try {
    const axiosResponse = await axiosInstance[method]<T, AxiosResponse<U>>(
      endpoint,
      data,
      {
        headers: {
          ...createHeaders(token),
          "Content-Type": "application/json",
        },
      },
    );
    return axiosResponse.data;
  } catch (error) {
    console.error(`Error ${method}ing ${endpoint}:`, error);
    throw error;
  }
}

export async function postApiResult<T, U>(
  axiosInstance: AxiosInstance,
  token: string,
  endpoint: string,
  data: T,
): Promise<U> {
  return await requestApiResultWithData(axiosInstance, token, endpoint, data, "post");
}

export async function putApiResult<T>(
  axiosInstance: AxiosInstance,
  token: string,
  endpoint: string,
  data: T,
): Promise<void> {
  return await requestApiResultWithData(axiosInstance, token, endpoint, data, "put");
}
